import React, { useRef, useEffect } from 'react';
import BaseModal from '../common/BaseModal';
import { zbudujSplitZKuponow } from '../../hooks/useCouponPayment';

const kwotaPL = (n) => (Number(n) || 0).toFixed(2).replace('.', ',');

/**
 * Platnosc kuponem (karta podarunkowa). K5: gdy pierwsza karta nie pokrywa
 * naleznosci, kasjerka dodaje kolejne - lista kart + reszta do pokrycia.
 * Gdy suma sald pokrywa naleznosc -> "Zaplac kuponami" (onPay), pod spodem
 * split_payments z N wpisami 'kupon'.
 */
const CouponPaymentModal = ({
  show,
  totalAmount,
  couponCode,
  couponValidating,
  onCouponCodeChange,
  onConfirm,
  onCancel,
  coupons = [],
  onRemoveCoupon,
  couponError = '',
  onPay
}) => {
  const inputRef = useRef(null);
  const isValidCode = couponCode && couponCode.length >= 4;
  const doZaplaty = Number(totalAmount) || 0;
  const { rows, pokryte, reszta } = zbudujSplitZKuponow(coupons, doZaplaty);
  const saKarty = coupons.length > 0;
  const pokrywa = saKarty && reszta <= 0;

  // Ustaw focus na input po otwarciu modalu (i po dodaniu karty)
  useEffect(() => {
    if (show && !pokrywa && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current && inputRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show, pokrywa, coupons.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isValidCode && !couponValidating) {
      e.preventDefault();
      onConfirm();
    }
  };

  const przyciskGlownyAktywny = pokrywa ? !couponValidating : (isValidCode && !couponValidating);
  const etykietaGlowna = couponValidating
    ? 'Sprawdzam...'
    : pokrywa
      ? 'Zapłać kuponami'
      : saKarty
        ? 'Dodaj kartę'
        : 'Sprawdź i zapłać';

  const footer = (
    <>
      <button
        onClick={onCancel}
        disabled={couponValidating}
        style={{
          flex: 1,
          padding: "6px 12px",
          border: "2px solid var(--text-muted)",
          backgroundColor: "var(--card-bg)",
          color: "var(--text-muted)",
          cursor: couponValidating ? "not-allowed" : "pointer",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        Anuluj
      </button>
      <button
        onClick={pokrywa ? onPay : onConfirm}
        disabled={!przyciskGlownyAktywny}
        style={{
          flex: 2,
          padding: "6px 12px",
          backgroundColor: przyciskGlownyAktywny ? "var(--purple)" : "var(--text-muted)",
          color: "#fff",
          cursor: przyciskGlownyAktywny ? "pointer" : "not-allowed",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        <i className="fas fa-check" style={{ marginRight: "0.5rem" }}></i>
        {etykietaGlowna}
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={show}
      onClose={onCancel}
      title={
        <>
          <i className="fas fa-ticket-alt" style={{ marginRight: "0.5rem" }}></i>
          Płatność kuponem
        </>
      }
      headerStyle={{ backgroundColor: "var(--purple)" }}
      size="md"
      footer={footer}
    >
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <div style={{ fontSize: "1.2rem", fontWeight: "600", color: "var(--text-secondary)" }}>
          Do zapłaty: {doZaplaty.toFixed(2)} zł
        </div>
      </div>

      {/* Lista dodanych kart */}
      {saKarty && (
        <div style={{ marginBottom: "0.75rem", display: "grid", gap: "0.35rem" }} data-testid="coupon-list">
          {coupons.map((c, idx) => {
            const wiersz = rows.find(r => r.couponCode === String(c.code || '').toUpperCase());
            return (
              <div key={c.code} style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "4px 10px",
                border: "1px solid var(--border)",
                borderLeft: "3px solid var(--purple)",
                borderRadius: "4px",
                backgroundColor: "var(--bg)",
                fontSize: "11px"
              }}>
                <span style={{ fontWeight: "600" }}>Karta {idx + 1}: {c.code}</span>
                <span style={{ color: "var(--text-muted)" }}>saldo {kwotaPL(c.value)} zł</span>
                <span style={{ marginLeft: "auto", color: "var(--success)", fontWeight: "600" }}>
                  {wiersz ? `użyte ${kwotaPL(wiersz.amount)} zł` : 'nieużyta'}
                </span>
                {onRemoveCoupon && (
                  <button
                    onClick={() => onRemoveCoupon(c.code)}
                    disabled={couponValidating}
                    aria-label={`Usuń kartę ${c.code}`}
                    style={{
                      padding: "4px 10px",
                      border: "1px solid var(--border)",
                      backgroundColor: "var(--card-bg)",
                      color: "var(--danger)",
                      fontSize: "11px",
                      cursor: "pointer"
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: "11px", textAlign: "right", color: pokrywa ? "var(--success)" : "var(--orange)", fontWeight: "600" }}>
            {pokrywa
              ? `Karty pokrywają całą należność (${kwotaPL(pokryte)} zł)`
              : `Pozostaje ${kwotaPL(reszta)} zł - dodaj kolejną kartę`}
          </div>
        </div>
      )}

      {!pokrywa && (
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500", fontSize: "12px" }}>
            {saKarty ? `Kod kolejnej karty (${coupons.length + 1}):` : 'Kod kuponu:'}
          </label>
          <input
            ref={inputRef}
            type="text"
            value={couponCode}
            onChange={(e) => onCouponCodeChange(e.target.value.toUpperCase())}
            onKeyDown={handleKeyDown}
            placeholder={saKarty ? "Zeskanuj kolejną kartę" : "Wprowadź kod kuponu"}
            aria-label="Kod kuponu"
            style={{
              width: "100%",
              padding: "0.75rem",
              border: "2px solid var(--border)",
              borderRadius: "8px",
              fontSize: "1rem",
              textAlign: "center",
              letterSpacing: "2px",
              fontWeight: "600",
            }}
            disabled={couponValidating}
          />
        </div>
      )}

      {couponError && (
        <div style={{
          padding: "0.4rem",
          backgroundColor: "var(--danger-light)",
          color: "var(--danger)",
          borderRadius: "4px",
          fontSize: "11px"
        }}>
          {couponError}
        </div>
      )}
    </BaseModal>
  );
};

export default React.memo(CouponPaymentModal);
